import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";

export function Account() {
  const { user, refreshMe } = useAuth();
  const [displayName, setDisplayName] = useState(user?.display_name || "");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [prefs, setPrefs] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDisplayName(user?.display_name || "");
    (async () => {
      try {
        const p = await api("/me/notifications/prefs");
        setPrefs(p.data || []);
      } catch {}
    })();
  }, [user]);

  async function saveProfile(e) {
    e.preventDefault(); setBusy(true);
    try {
      await api("/me/profile", { method: "PATCH", body: { display_name: displayName } });
      toast.success("Profile saved");
      await refreshMe();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  }

  async function changePassword(e) {
    e.preventDefault(); setBusy(true);
    try {
      await api("/me/password", { method: "PATCH", body: { current_password: curPw, new_password: newPw } });
      toast.success("Password updated");
      setCurPw(""); setNewPw("");
      await refreshMe();
    } catch (e) { toast.error(e?.problem?.detail || e.message); }
    finally { setBusy(false); }
  }

  return (
    <Layout>
      <h1 className="text-3xl">Account</h1>
      <div className="mt-6 grid lg:grid-cols-2 gap-5">
        <form data-testid={TID.profileForm} onSubmit={saveProfile} className="avcp-card p-6 grid gap-4">
          <h2 className="text-lg">Profile</h2>
          <label className="grid gap-1.5">
            <span className="text-sm font-medium">Email</span>
            <input className="avcp-input" value={user?.email || ""} disabled />
          </label>
          <label className="grid gap-1.5">
            <span className="text-sm font-medium">Display name</span>
            <input data-testid={TID.profileDisplayName} className="avcp-input"
              value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </label>
          <button data-testid={TID.profileSave} className="avcp-btn primary" disabled={busy}>Save</button>
        </form>

        <form onSubmit={changePassword} className="avcp-card p-6 grid gap-4">
          <h2 className="text-lg">Change password</h2>
          <label className="grid gap-1.5">
            <span className="text-sm font-medium">Current password</span>
            <input data-testid={TID.passwordCurrent} className="avcp-input" type="password"
              value={curPw} onChange={(e) => setCurPw(e.target.value)} required />
          </label>
          <label className="grid gap-1.5">
            <span className="text-sm font-medium">New password</span>
            <input data-testid={TID.passwordNew} className="avcp-input" type="password" minLength={12}
              value={newPw} onChange={(e) => setNewPw(e.target.value)} required />
          </label>
          <button data-testid={TID.passwordSubmit} className="avcp-btn primary" disabled={busy || !curPw || !newPw}>
            Update password
          </button>
        </form>
      </div>

      <div className="mt-6 avcp-card p-6">
        <h2 className="text-lg">Notification preferences</h2>
        <p className="mt-1 text-sm text-[var(--avcp-ink-soft)]">
          Per AR-13. Phase 2 manages auth-class events; business events arrive in Phase 8.
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="avcp-table">
            <thead><tr><th>Event</th><th>Channel</th><th>Enabled</th></tr></thead>
            <tbody>
              {prefs.map((p) => (
                <tr key={`${p.event_kind}-${p.channel}`}>
                  <td className="font-mono text-xs">{p.event_kind}</td>
                  <td>{p.channel}</td>
                  <td>
                    <input type="checkbox" checked={p.enabled} onChange={async (e) => {
                      const updates = [{ event_kind: p.event_kind, channel: p.channel, enabled: e.target.checked }];
                      await api("/me/notifications/prefs", { method: "PATCH", body: { updates } });
                      setPrefs((cur) => cur.map((x) => x === p ? { ...x, enabled: e.target.checked } : x));
                    }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export function Security() {
  const { user, signoutAll } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [disablePw, setDisablePw] = useState("");
  const [busy, setBusy] = useState(false);

  async function reload() {
    try { setSessions((await api("/auth/sessions")) || []); } catch {}
  }
  useEffect(() => { reload(); }, []);

  async function startMfa() {
    setBusy(true);
    try { setMfa(await api("/auth/mfa/setup", { method: "POST" })); }
    finally { setBusy(false); }
  }
  async function enableMfa() {
    setBusy(true);
    try {
      const r = await api("/auth/mfa/enable", { method: "POST", body: { code } });
      setBackupCodes(r.backup_codes);
      setMfa(null); setCode("");
      toast.success("MFA enabled");
    } catch (e) { toast.error(e?.problem?.detail || e.message); }
    finally { setBusy(false); }
  }
  async function disableMfa() {
    setBusy(true);
    try {
      await api("/auth/mfa/disable", { method: "POST", body: { current_password: disablePw, code } });
      setDisablePw(""); setCode("");
      toast.success("MFA disabled");
    } catch (e) { toast.error(e?.problem?.detail || e.message); }
    finally { setBusy(false); }
  }
  async function revokeSession(id) {
    await api(`/auth/sessions/${id}`, { method: "DELETE" });
    await reload();
  }

  return (
    <Layout>
      <h1 className="text-3xl">Security</h1>
      <div className="mt-6 grid lg:grid-cols-2 gap-5">
        <div className="avcp-card p-6">
          <h2 className="text-lg">Multi-factor authentication</h2>
          <p className="mt-1 text-sm text-[var(--avcp-ink-soft)]">
            {user?.mfa_enabled ? "MFA is enabled on this account." : "Enroll a TOTP app (e.g. 1Password, Aegis, Authy)."}
          </p>
          {!user?.mfa_enabled && !mfa && (
            <button data-testid={TID.mfaSetupBtn} className="avcp-btn primary mt-4" onClick={startMfa} disabled={busy}>
              Set up MFA
            </button>
          )}
          {mfa && (
            <div className="mt-4 grid gap-3">
              <div className="text-sm">
                Scan or copy the secret below into your authenticator app.
              </div>
              <div className="font-mono text-xs break-all p-3 bg-[var(--avcp-primary-soft)] rounded">{mfa.secret}</div>
              <label className="grid gap-1.5">
                <span className="text-sm font-medium">Enter 6-digit code</span>
                <input data-testid={TID.mfaCodeInput} className="avcp-input font-mono" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} />
              </label>
              <button data-testid={TID.mfaEnableBtn} className="avcp-btn primary" onClick={enableMfa} disabled={busy || code.length !== 6}>
                Enable MFA
              </button>
            </div>
          )}
          {backupCodes && (
            <div className="mt-4">
              <div className="avcp-pill success">MFA enabled</div>
              <p className="mt-2 text-sm">Save these single-use backup codes:</p>
              <div className="mt-2 font-mono text-xs grid grid-cols-2 gap-1 p-3 bg-[var(--avcp-primary-soft)] rounded">
                {backupCodes.map((c) => <div key={c}>{c}</div>)}
              </div>
            </div>
          )}
          {user?.mfa_enabled && (
            <div className="mt-6 grid gap-3 border-t border-[var(--avcp-line)] pt-4">
              <h3 className="text-sm font-semibold">Disable MFA</h3>
              <input className="avcp-input" type="password" placeholder="Current password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} />
              <input className="avcp-input font-mono" maxLength={6} placeholder="Current 6-digit code" value={code} onChange={(e) => setCode(e.target.value)} />
              <button data-testid={TID.mfaDisableBtn} className="avcp-btn danger" onClick={disableMfa} disabled={busy || !disablePw || code.length !== 6}>
                Disable MFA
              </button>
            </div>
          )}
        </div>

        <div className="avcp-card p-6">
          <h2 className="text-lg">Active sessions</h2>
          <p className="mt-1 text-sm text-[var(--avcp-ink-soft)]">Revoke a specific session or all of them.</p>
          <ul className="mt-4 grid gap-3">
            {sessions.length === 0 && <div className="text-sm text-[var(--avcp-ink-soft)]">No active sessions.</div>}
            {sessions.map((s) => (
              <li key={s.id} data-testid={TID.sessionRow} className="flex items-center justify-between border-b border-[var(--avcp-line)] pb-3">
                <div>
                  <div className="text-sm font-mono">{s.client_type || "web"} · {s.ip_last_seen || "—"}</div>
                  <div className="text-xs text-[var(--avcp-ink-soft)]">last used {s.last_used_at}</div>
                </div>
                <button className="avcp-btn ghost" onClick={() => revokeSession(s.id)}>Revoke</button>
              </li>
            ))}
          </ul>
          <button data-testid={TID.logoutAll} className="avcp-btn danger mt-4" onClick={signoutAll}>
            Log out of all devices
          </button>
        </div>
      </div>
    </Layout>
  );
}
